<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, provide } from 'vue'
import { useRoute } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { usePersonnelStore } from '@/stores/personnel'
import { useTeamsStore } from '@/stores/teams'
import { useAssignmentsStore } from '@/stores/assignments'
import { useRadioLogStore } from '@/stores/radioLog'
import { useSubjectsStore } from '@/stores/subjects'
import { useIwiStore } from '@/stores/iwi'
import { usePriorityTrafficStore } from '@/stores/priorityTraffic'
import { useAuthStore } from '@/stores/auth'
import { useCaltopoStore } from '@/stores/caltopo'
import { useCommsStore } from '@/stores/comms'
import AppHeader from '@/components/layout/AppHeader.vue'
import IncidentSidebar from '@/components/layout/IncidentSidebar.vue'
import TelemetryRibbon from '@/components/layout/TelemetryRibbon.vue'
import CheckInModal from '@/components/personnel/CheckInModal.vue'
import FosButton from '@/components/ui/FosButton.vue'

const route = useRoute()
const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const personnelStore = usePersonnelStore()
const teamsStore = useTeamsStore()
const assignmentsStore = useAssignmentsStore()
const radioLogStore = useRadioLogStore()
const subjectsStore = useSubjectsStore()
const iwiStore = useIwiStore()
const ptiStore = usePriorityTrafficStore()
const authStore = useAuthStore()
const caltopoStore = useCaltopoStore()
const commsStore = useCommsStore()

const showCheckIn = ref(false)

const isCheckedIn = computed(() => {
  if (!authStore.currentUser) return true
  return personnelStore.roster.some(
    (p) => p.user_id === authStore.currentUser!.uid && p.time_out === null,
  )
})

const incidentId = route.params.id as string
provide('incidentId', incidentId)

onMounted(() => {
  incidentStore.subscribeToIncident(incidentId)
  opsStore.subscribeToOps(incidentId)
  subjectsStore.subscribeToSubjects(incidentId)
})

watch(
  () => opsStore.selectedOpId,
  (opId) => {
    if (opId) {
      personnelStore.subscribeToPersonnel(incidentId, opId)
      teamsStore.subscribeToTeams(incidentId, opId)
      radioLogStore.subscribeToLog(incidentId, opId)
      iwiStore.subscribeToIwi(incidentId, opId)
      ptiStore.subscribeToPriorityTraffic(incidentId, opId)
      commsStore.subscribe(incidentId, opId)
    }
  },
)

watch(
  () => opsStore.selectedOp,
  (op) => {
    if (op?.caltopo_map_id) {
      caltopoStore.joinSession(
        incidentId,
        op.op_id,
        op.caltopo_map_id,
        op.caltopo_op_id ?? null,
        op.caltopo_poll_interval_ms ?? undefined,
      )
    } else {
      caltopoStore.leaveSession()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  incidentStore.unsubscribeAll()
  opsStore.teardown()
  personnelStore.teardown()
  teamsStore.teardown()
  assignmentsStore.teardown()
  radioLogStore.teardown()
  subjectsStore.teardown()
  iwiStore.teardown()
  ptiStore.teardown()
  commsStore.teardown()
  caltopoStore.leaveSession()
})
</script>

<template>
  <div class="h-screen overflow-hidden text-on-surface">
    <AppHeader />
    <IncidentSidebar />
    <TelemetryRibbon />
    <main class="md:ml-20 mt-12 mb-14 md:mb-8 h-[calc(100vh-6.5rem)] md:h-[calc(100vh-5rem)] overflow-hidden flex flex-col">
      <div
        v-if="!isCheckedIn && authStore.currentUser"
        class="flex items-center gap-3 px-4 py-2 bg-surface-container-high border-b border-border border-l-2 border-l-primary-container shrink-0"
      >
        <span class="material-symbols-outlined text-outline text-[16px]">lock</span>
        <span class="font-mono text-xs uppercase tracking-widest text-on-surface-variant flex-1">
          Read-only — You are not checked in to this operational period
        </span>
        <FosButton variant="primary" size="sm" @click="showCheckIn = true">Check In</FosButton>
      </div>
      <div class="flex-1 overflow-hidden">
        <RouterView />
      </div>
    </main>
  </div>

  <CheckInModal
    v-if="incidentStore.currentIncident"
    v-model:visible="showCheckIn"
    :incident-id="incidentId"
  />
</template>
